// Chart — a small inline-SVG chart set for the reports.
//
// WHY NOT A CHARTING LIBRARY. Adding recharts/visx is a new third-party
// dependency (ask-first, CLAUDE.md §7) and costs ~500KB against a <250KB
// initial-route budget. These three shapes — a line/area trend, a bar
// comparison, a donut breakdown — cover every report in the console. Hand-rolled
// SVG also means we control RTL mirroring, reduced-motion, and colour tokens.
//
// Conventions shared by all three:
//  · The series colour comes from the token palette, never a hardcoded hex.
//  · Every chart also renders an accessible <table> in a visually-hidden
//    container, so the data is readable by screen readers and copy-pasteable.
//    A chart nobody can read is not a chart, it is decoration.
//  · An empty series renders an explicit "no data" state, never an empty box.

import { cn } from '@fajrarabi/ui';
import { useId, useMemo, type ReactNode } from 'react';

export interface SeriesPoint {
  /** ISO date or short label for the x axis. */
  label: string;
  value: number;
}

// Categorical palette. Ordered so the first three are maximally distinct —
// most breakdowns here have two or three slices.
export const CHART_COLORS = [
  'var(--color-brand-500)',
  'var(--color-success-500)',
  'var(--color-warning-500)',
  'var(--color-error-500)',
  'var(--color-admin-400)',
  'var(--color-brand-700)',
] as const;

function niceMax(value: number): number {
  if (value <= 0) return 1;
  // Round the axis top up to 1/2/5 × 10^n so gridlines land on readable numbers.
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

function ChartFrame({
  title,
  children,
  data,
  valueFormatter,
  className,
  height = 200,
  action,
}: {
  title: string;
  children: ReactNode;
  data: readonly SeriesPoint[];
  valueFormatter: (v: number) => string;
  className?: string;
  height?: number;
  action?: ReactNode;
}) {
  return (
    <figure className={cn('rounded-xl border border-admin-200 bg-white p-4', className)}>
      <figcaption className="mb-3 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-admin-800">{title}</span>
        {action}
      </figcaption>

      {data.length === 0 ? (
        <div
          style={{ height }}
          className="grid place-items-center rounded-lg bg-admin-50 text-sm text-admin-400"
        >
          No data for this period
        </div>
      ) : (
        <>
          {children}
          {/* The same data as a table, for assistive tech and copy-paste. */}
          <table className="sr-only">
            <caption>{title}</caption>
            <thead>
              <tr>
                <th scope="col">Label</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.label}>
                  <th scope="row">{d.label}</th>
                  <td>{valueFormatter(d.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </figure>
  );
}

export interface LineChartProps {
  title: string;
  data: readonly SeriesPoint[];
  valueFormatter?: (v: number) => string;
  height?: number;
  color?: string;
  className?: string;
  action?: ReactNode;
}

/** Trend over time. Area-filled line; good for revenue and signups per day. */
export function LineChart({
  title,
  data,
  valueFormatter = (v) => v.toLocaleString('en-US'),
  height = 200,
  color = CHART_COLORS[0],
  className,
  action,
}: LineChartProps) {
  const gradientId = useId();
  const W = 600;
  const H = height;
  const PAD = { top: 8, right: 8, bottom: 22, left: 44 };

  const { max, points, areaPath, linePath, gridLines } = useMemo(() => {
    const maxValue = niceMax(Math.max(0, ...data.map((d) => d.value)));
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    const x = (i: number) =>
      PAD.left + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
    const y = (v: number) => PAD.top + innerH - (v / maxValue) * innerH;

    const pts = data.map((d, i) => ({ x: x(i), y: y(d.value), ...d }));
    const line = pts
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' ');
    const area =
      pts.length > 0
        ? `${line} L${pts[pts.length - 1]!.x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} L${pts[0]!.x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} Z`
        : '';

    const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
      y: PAD.top + innerH - f * innerH,
      value: maxValue * f,
    }));

    return { max: maxValue, points: pts, areaPath: area, linePath: line, gridLines: grid };
  }, [data, H]);

  return (
    <ChartFrame
      title={title}
      data={data}
      valueFormatter={valueFormatter}
      className={className}
      height={height}
      action={action}
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="presentation" style={{ height }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridLines.map((g) => (
          <g key={g.y}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={g.y}
              y2={g.y}
              stroke="var(--color-admin-200)"
              strokeWidth="1"
            />
            <text
              x={PAD.left - 6}
              y={g.y + 3.5}
              textAnchor="end"
              className="fill-admin-400 text-[10px] tabular-nums"
            >
              {valueFormatter(g.value)}
            </text>
          </g>
        ))}

        {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
        {linePath && (
          <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
        )}

        {points.map((p) => (
          <circle key={p.label} cx={p.x} cy={p.y} r="2.5" fill={color}>
            <title>{`${p.label}: ${valueFormatter(p.value)}`}</title>
          </circle>
        ))}

        {/* First / middle / last x labels only — a dense axis is unreadable at
            this width and the tooltip carries the exact date. */}
        {points.length > 0 &&
          [0, Math.floor(points.length / 2), points.length - 1]
            .filter((i, idx, arr) => arr.indexOf(i) === idx)
            .map((i) => (
              <text
                key={`x-${i}`}
                x={points[i]!.x}
                y={H - 6}
                textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
                className="fill-admin-400 text-[10px]"
              >
                {points[i]!.label}
              </text>
            ))}

        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={H - PAD.bottom}
          y2={H - PAD.bottom}
          stroke="var(--color-admin-300)"
          strokeWidth="1"
        />
        <desc>{`Maximum ${valueFormatter(max)}`}</desc>
      </svg>
    </ChartFrame>
  );
}

/** Comparison across categories. Horizontal, so long labels stay readable. */
export function BarChart({
  title,
  data,
  valueFormatter = (v) => v.toLocaleString('en-US'),
  className,
  color = CHART_COLORS[0],
  action,
}: {
  title: string;
  data: readonly SeriesPoint[];
  valueFormatter?: (v: number) => string;
  className?: string;
  color?: string;
  action?: ReactNode;
}) {
  const max = useMemo(() => niceMax(Math.max(0, ...data.map((d) => d.value))), [data]);

  return (
    <ChartFrame
      title={title}
      data={data}
      valueFormatter={valueFormatter}
      className={className}
      action={action}
    >
      {/* CSS bars rather than SVG: the labels are real text that wraps and
          respects the reading direction for free. */}
      <div className="flex flex-col gap-2.5">
        {data.map((d) => (
          <div key={d.label} className="grid grid-cols-[8rem_1fr_auto] items-center gap-3">
            <span className="truncate text-xs text-admin-600" title={d.label}>
              {d.label}
            </span>
            <div className="h-5 overflow-hidden rounded bg-admin-100">
              <div
                className="h-full rounded transition-[width] duration-500 motion-reduce:transition-none"
                style={{
                  width: `${max === 0 ? 0 : (d.value / max) * 100}%`,
                  backgroundColor: color,
                }}
              />
            </div>
            <span className="text-xs font-medium tabular-nums text-admin-700">
              {valueFormatter(d.value)}
            </span>
          </div>
        ))}
      </div>
    </ChartFrame>
  );
}

/** Composition of a whole. Capped at six slices before an "Other" bucket. */
export function DonutChart({
  title,
  data,
  valueFormatter = (v) => v.toLocaleString('en-US'),
  className,
  action,
}: {
  title: string;
  data: readonly SeriesPoint[];
  valueFormatter?: (v: number) => string;
  className?: string;
  action?: ReactNode;
}) {
  const { slices, total } = useMemo(() => {
    const sum = data.reduce((acc, d) => acc + d.value, 0);
    let offset = 0;
    const out = data.map((d, i) => {
      const fraction = sum === 0 ? 0 : d.value / sum;
      const slice = { ...d, fraction, offset, color: CHART_COLORS[i % CHART_COLORS.length]! };
      offset += fraction;
      return slice;
    });
    return { slices: out, total: sum };
  }, [data]);

  const R = 42;
  const CIRC = 2 * Math.PI * R;

  return (
    <ChartFrame
      title={title}
      data={data}
      valueFormatter={valueFormatter}
      className={className}
      action={action}
    >
      <div className="flex flex-wrap items-center gap-5">
        <svg viewBox="0 0 100 100" className="h-32 w-32 shrink-0 -rotate-90" role="presentation">
          <circle
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke="var(--color-admin-100)"
            strokeWidth="14"
          />
          {slices.map((s) => (
            <circle
              key={s.label}
              cx="50"
              cy="50"
              r={R}
              fill="none"
              stroke={s.color}
              strokeWidth="14"
              strokeDasharray={`${(s.fraction * CIRC).toFixed(2)} ${CIRC.toFixed(2)}`}
              strokeDashoffset={`${(-s.offset * CIRC).toFixed(2)}`}
            >
              <title>{`${s.label}: ${valueFormatter(s.value)}`}</title>
            </circle>
          ))}
        </svg>

        <ul className="min-w-0 flex-1 space-y-1.5">
          {slices.map((s) => (
            <li key={s.label} className="flex items-center gap-2 text-xs">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: s.color }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-admin-600">{s.label}</span>
              <span className="font-medium tabular-nums text-admin-800">
                {valueFormatter(s.value)}
              </span>
              <span className="w-10 text-end tabular-nums text-admin-400">
                {total === 0 ? '0%' : `${Math.round((s.value / total) * 100)}%`}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </ChartFrame>
  );
}

/** Tiny inline trend for stat tiles. No axes, no labels. */
export function Sparkline({
  data,
  color = CHART_COLORS[0],
  className,
}: {
  data: readonly number[];
  color?: string;
  className?: string;
}) {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const path = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 24 - ((v - min) / span) * 22 - 1;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      className={cn('h-6 w-full', className)}
      aria-hidden
    >
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
